<script setup lang="ts">
import {
  computed,
  nextTick,
  ref,
  useTemplateRef,
  watch,
  watchEffect,
} from "vue";
import {
  isJust,
  isNothing,
  just,
  matchMaybe,
  nothing,
  toMaybe,
  type Maybe,
} from "../lib/maybe";
import { displayFileName } from "../lib/fileNames";
import type { MapLandmark, MapSceneLink } from "../lib/mapDocument";
import ZwButton from "./ui/ZwButton.vue";
import ZwField from "./ui/ZwField.vue";
import ZwInput from "./ui/ZwInput.vue";
import ZwSearchableMenu from "./ui/ZwSearchableMenu.vue";

const props = withDefaults(
  defineProps<{
    landmark: MapLandmark;
    readonly: boolean;
    entityOptions: { value: string; label: string }[];
    /** Manuscript scenes for `sceneLinks` (path → label). */
    sceneOptions: { value: string; label: string }[];
    /** False when entityFile is set but missing from the project. */
    entityResolved: boolean;
    /** Display title for the bound entry (H1 / stem). */
    boundEntityTitle: string;
    /** HTML body preview for the bound entry (entity-peek style). */
    boundEntityBodyHtml: string;
    /** When true, sit inside the map sidebar (no float / close chrome). */
    embedded?: boolean;
  }>(),
  { embedded: false },
);

const emit = defineEmits<{
  "update:caption": [value: string];
  "update:notes": [value: string];
  "update:entityFile": [value: string];
  clearEntity: [];
  openEntry: [];
  addSceneLink: [file: string];
  replaceSceneLink: [index: number, file: string];
  removeSceneLink: [index: number];
  openScene: [file: string];
  close: [];
  /** Push undo before the first edit in a field session. */
  beginEdit: [];
}>();

const entityMenuOpen = ref(false);
const addSceneMenuOpen = ref(false);
/** Index of a broken scene link whose Relink menu is open. */
const relinkSceneIndex = ref<Maybe<number>>(nothing);
/** Bound entry body is expanded past the clamped peek. */
const entryBodyExpanded = ref(false);
/** True when clamped body overflows (show More). */
const entryBodyClamped = ref(false);

const captionModel = computed({
  get: (): string => props.landmark.caption ?? "",
  set: (value: string): void => {
    emit("update:caption", value);
  },
});

const notesModel = computed({
  get: (): string => props.landmark.notes ?? "",
  set: (value: string): void => {
    emit("update:notes", value);
  },
});

function optionalLandmarkText(value: MapLandmark["entityFile"]): Maybe<string> {
  const textM = toMaybe(value);
  if (isNothing(textM) || textM.value.length === 0) return nothing;
  return just(textM.value);
}

const boundEntityFile = computed(() =>
  optionalLandmarkText(props.landmark.entityFile),
);

const hasBoundEntity = computed(() => isJust(boundEntityFile.value));

const showBroken = computed(
  () => hasBoundEntity.value === true && props.entityResolved === false,
);

const showBoundCard = computed(
  () => hasBoundEntity.value === true && props.entityResolved === true,
);

const boundTitle = computed(() => {
  if (props.boundEntityTitle.trim().length > 0) return props.boundEntityTitle;
  return matchMaybe(boundEntityFile.value, {
    nothing: () => "Entry",
    just: (file) => displayFileName(file),
  });
});

const pickerLabel = computed(() => {
  if (showBroken.value === false) return "Link entry…";
  return matchMaybe(boundEntityFile.value, {
    nothing: () => "Link entry…",
    just: (file) => displayFileName(file),
  });
});

function onPickEntity(value: string): void {
  emit("beginEdit");
  emit("update:entityFile", value);
}

function onClearEntity(): void {
  emit("beginEdit");
  emit("clearEntity");
}

const sceneLinks = computed(
  (): MapSceneLink[] => props.landmark.sceneLinks ?? [],
);

const sceneLabelByPath = computed(() => {
  const map = new Map<string, string>();
  for (const opt of props.sceneOptions) {
    map.set(opt.value, opt.label);
  }
  return map;
});

const knownScenePaths = computed(
  () => new Set(props.sceneOptions.map((o) => o.value)),
);

function sceneLinkResolved(file: string): boolean {
  // Avoid a broken flash while the shell has not passed options yet.
  if (props.sceneOptions.length === 0) return true;
  return knownScenePaths.value.has(file);
}

function sceneDisplayLabel(file: string): string {
  return sceneLabelByPath.value.get(file) ?? displayFileName(file);
}

const addSceneOptions = computed(() => {
  const linked = new Set(sceneLinks.value.map((link) => link.file));
  return props.sceneOptions.filter((opt) => linked.has(opt.value) === false);
});

function onAddScene(file: string): void {
  emit("beginEdit");
  emit("addSceneLink", file);
  addSceneMenuOpen.value = false;
}

function onRelinkScene(index: number, file: string): void {
  emit("beginEdit");
  emit("replaceSceneLink", index, file);
  relinkSceneIndex.value = nothing;
}

function onRemoveScene(index: number): void {
  emit("beginEdit");
  emit("removeSceneLink", index);
}

function setRelinkOpen(index: number, open: boolean): void {
  relinkSceneIndex.value = open ? just(index) : nothing;
}

function isRelinkOpen(index: number): boolean {
  return matchMaybe(relinkSceneIndex.value, {
    nothing: () => false,
    just: (openIndex) => openIndex === index,
  });
}

/** Project-owned entry HTML (same trust model as entity peek cards). */
const boundBodyHost = useTemplateRef<HTMLElement>("boundBodyHost");

async function measureEntryBodyClamp(): Promise<void> {
  await nextTick();
  const elM = toMaybe(boundBodyHost.value);
  if (isNothing(elM)) {
    entryBodyClamped.value = false;
    return;
  }
  // Expanded: keep the prior overflow flag so Less stays available.
  if (entryBodyExpanded.value === true) return;
  const el = elM.value;
  entryBodyClamped.value = el.scrollHeight > el.clientHeight + 1;
}

watchEffect(() => {
  const elM = toMaybe(boundBodyHost.value);
  if (isNothing(elM)) return;
  elM.value.innerHTML = props.boundEntityBodyHtml;
  void measureEntryBodyClamp();
});

watch(
  () => [props.landmark.id, props.landmark.entityFile] as const,
  () => {
    entryBodyExpanded.value = false;
    entryBodyClamped.value = false;
  },
);

function toggleEntryBodyExpanded(): void {
  entryBodyExpanded.value = entryBodyExpanded.value === false;
  void measureEntryBodyClamp();
}
</script>

<template>
  <aside
    class="map-landmark-panel"
    :class="{ 'map-landmark-panel-embedded': embedded }"
    aria-label="Landmark"
  >
    <header class="map-landmark-panel-head">
      <h2 class="map-landmark-panel-title">Landmark</h2>
      <button
        v-if="embedded === false"
        type="button"
        class="map-landmark-panel-close"
        aria-label="Close landmark panel"
        @click="emit('close')"
      >
        ×
      </button>
    </header>

    <div class="map-landmark-stack">
      <ZwField label="Caption" :for-id="nothing">
        <ZwInput
          :id="nothing"
          v-model="captionModel"
          :label="nothing"
          :disabled="readonly"
          placeholder="Name on the map"
          @focus="emit('beginEdit')"
        />
      </ZwField>

      <ZwField label="Entry" :for-id="nothing">
        <div v-if="showBoundCard" class="map-landmark-bound">
          <div class="map-landmark-link-row">
            <button
              type="button"
              class="map-landmark-link"
              :title="`Open ${boundTitle}`"
              @click="emit('openEntry')"
            >
              {{ boundTitle }}
            </button>
            <ZwButton
              size="sm"
              variant="ghost"
              :disabled="readonly"
              aria-label="Unlink entry"
              title="Unlink"
              @click="onClearEntity"
            >
              ×
            </ZwButton>
          </div>
          <div
            v-if="boundEntityBodyHtml.length > 0"
            ref="boundBodyHost"
            class="map-landmark-bound-prose"
            :class="{ 'map-landmark-bound-prose-expanded': entryBodyExpanded }"
          />
          <button
            v-if="
              boundEntityBodyHtml.length > 0 &&
              (entryBodyClamped || entryBodyExpanded)
            "
            type="button"
            class="map-landmark-more"
            @click.stop="toggleEntryBodyExpanded"
          >
            {{ entryBodyExpanded ? "Less" : "More" }}
          </button>
          <p
            v-else-if="boundEntityBodyHtml.length === 0"
            class="map-landmark-hint"
          >
            No entry text yet.
          </p>
        </div>

        <div v-else class="map-landmark-pick-block">
          <ZwSearchableMenu
            v-model:open="entityMenuOpen"
            class="map-landmark-menu"
            :options="entityOptions"
            :disabled="readonly || entityOptions.length === 0"
            search-placeholder="Search entries…"
            :menu-label="just('Entry')"
            @select="onPickEntity"
          >
            <template #trigger="{ toggle, attrs }">
              <button
                type="button"
                class="map-landmark-pick"
                :class="{ 'map-landmark-pick-broken': showBroken }"
                v-bind="attrs"
                :disabled="readonly || entityOptions.length === 0"
                @click="toggle()"
              >
                {{ pickerLabel }}
              </button>
            </template>
          </ZwSearchableMenu>
          <p v-if="showBroken" class="map-landmark-error">
            Entry missing.
            <button
              type="button"
              class="map-landmark-inline-action"
              :disabled="readonly"
              @click="onClearEntity"
            >
              Clear
            </button>
          </p>
        </div>
      </ZwField>

      <ZwField label="Scenes" :for-id="nothing">
        <ul v-if="sceneLinks.length > 0" class="map-landmark-list">
          <li
            v-for="(link, index) in sceneLinks"
            :key="`${link.file}-${index}`"
            class="map-landmark-list-item"
          >
            <div class="map-landmark-link-row">
              <button
                type="button"
                class="map-landmark-link"
                :title="`Open ${sceneDisplayLabel(link.file)}`"
                :disabled="sceneLinkResolved(link.file) === false"
                @click="emit('openScene', link.file)"
              >
                {{ sceneDisplayLabel(link.file) }}
              </button>
              <ZwButton
                size="sm"
                variant="ghost"
                :disabled="readonly"
                aria-label="Remove scene link"
                title="Remove"
                @click="onRemoveScene(index)"
              >
                ×
              </ZwButton>
            </div>
            <div
              v-if="sceneLinkResolved(link.file) === false"
              class="map-landmark-broken-actions"
            >
              <p class="map-landmark-error">Scene missing.</p>
              <ZwSearchableMenu
                :open="isRelinkOpen(index)"
                class="map-landmark-menu"
                :options="addSceneOptions"
                :disabled="readonly"
                search-placeholder="Search scenes…"
                :menu-label="just('Scene')"
                :empty-label="
                  sceneOptions.length === 0
                    ? 'No manuscript scenes yet'
                    : 'No matching scenes'
                "
                @update:open="setRelinkOpen(index, $event)"
                @select="onRelinkScene(index, $event)"
              >
                <template #trigger="{ toggle, attrs }">
                  <button
                    type="button"
                    class="map-landmark-pick map-landmark-pick-compact"
                    v-bind="attrs"
                    :disabled="readonly"
                    @click="toggle()"
                  >
                    Relink…
                  </button>
                </template>
              </ZwSearchableMenu>
            </div>
          </li>
        </ul>
        <ZwSearchableMenu
          v-model:open="addSceneMenuOpen"
          class="map-landmark-menu"
          :options="addSceneOptions"
          :disabled="readonly"
          search-placeholder="Search scenes…"
          :menu-label="just('Scene')"
          :empty-label="
            sceneOptions.length === 0
              ? 'No manuscript scenes yet'
              : 'No matching scenes'
          "
          @select="onAddScene"
        >
          <template #trigger="{ toggle, attrs }">
            <button
              type="button"
              class="map-landmark-pick"
              v-bind="attrs"
              :disabled="readonly"
              @click="toggle()"
            >
              Link scene…
            </button>
          </template>
        </ZwSearchableMenu>
      </ZwField>

      <ZwField label="Notes" :for-id="nothing">
        <textarea
          v-model="notesModel"
          class="map-landmark-notes"
          rows="2"
          :disabled="readonly"
          placeholder="Short place note"
          writingsuggestions="false"
          @focus="emit('beginEdit')"
        />
      </ZwField>
    </div>
  </aside>
</template>

<style scoped>
.map-landmark-panel {
  position: absolute;
  top: -1px;
  right: 0;
  bottom: 0;
  z-index: 2;
  box-sizing: border-box;
  width: 16.5rem;
  overflow: auto;
  padding: 0.65rem 0.75rem 1rem;
  border: 0;
  border-left: 1px solid var(--line);
  border-top: 1px solid var(--line);
  border-radius: 0;
  background: var(--bg-raised);
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
}

.map-landmark-panel-embedded {
  position: relative;
  top: auto;
  right: auto;
  bottom: auto;
  z-index: auto;
  width: 100%;
  flex: 1;
  min-height: 0;
  border: 0;
  border-radius: 0;
  padding: 0.65rem 0.75rem 1rem;
}

.map-landmark-panel-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  flex-shrink: 0;
}

.map-landmark-panel-title {
  margin: 0;
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--text-strong);
}

.map-landmark-panel-close {
  border: 0;
  background: transparent;
  color: var(--text-muted);
  font-size: 1.15rem;
  line-height: 1;
  padding: 0.15rem 0.35rem;
  border-radius: 0.25rem;
  cursor: pointer;
}

.map-landmark-panel-close:hover {
  background: var(--hover-fill);
  color: var(--text);
}

.map-landmark-stack {
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
  min-width: 0;
}

.map-landmark-notes {
  width: 100%;
  font: inherit;
  font-size: 0.8125rem;
  line-height: 1.4;
  padding: 0.35rem 0.45rem;
  border: 1px solid var(--line);
  border-radius: 0.25rem;
  background: var(--bg);
  color: var(--text);
  resize: vertical;
  min-height: 2.75rem;
}

.map-landmark-notes:disabled {
  opacity: 0.6;
}

.map-landmark-menu {
  width: 100%;
}

.map-landmark-pick-block {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
}

.map-landmark-pick {
  width: 100%;
  text-align: left;
  font: inherit;
  font-size: 0.8125rem;
  padding: 0.35rem 0.45rem;
  border: 1px solid var(--line);
  border-radius: 0.25rem;
  background: var(--bg);
  color: var(--text);
  cursor: pointer;
}

.map-landmark-pick-compact {
  width: auto;
  align-self: flex-start;
}

.map-landmark-pick:hover:not(:disabled) {
  background: var(--hover-fill);
}

.map-landmark-pick:disabled {
  opacity: 0.55;
  cursor: default;
}

.map-landmark-pick-broken {
  border-color: color-mix(in oklch, var(--danger, #a33) 55%, var(--line));
  color: var(--danger, #a33);
}

.map-landmark-error {
  margin: 0;
  font-size: 0.75rem;
  line-height: 1.35;
  color: var(--danger, #a33);
}

.map-landmark-inline-action {
  appearance: none;
  border: 0;
  background: transparent;
  padding: 0;
  margin: 0 0 0 0.25rem;
  font: inherit;
  font-size: inherit;
  color: inherit;
  text-decoration: underline;
  cursor: pointer;
}

.map-landmark-inline-action:disabled {
  opacity: 0.55;
  cursor: default;
  text-decoration: none;
}

.map-landmark-hint {
  margin: 0;
  font-size: 0.75rem;
  line-height: 1.4;
  color: var(--text-muted);
}

.map-landmark-bound {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  min-width: 0;
}

.map-landmark-link-row {
  display: flex;
  align-items: flex-start;
  gap: 0.15rem;
  min-width: 0;
}

.map-landmark-link {
  flex: 1;
  min-width: 0;
  appearance: none;
  border: 0;
  background: transparent;
  padding: 0.2rem 0;
  text-align: left;
  font: inherit;
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--text-strong);
  cursor: pointer;
  text-decoration: underline;
  text-decoration-color: color-mix(
    in oklch,
    var(--text-muted) 40%,
    transparent
  );
  text-underline-offset: 0.15em;
}

.map-landmark-link:hover:not(:disabled) {
  color: var(--text);
  text-decoration-color: var(--text-muted);
}

.map-landmark-link:disabled {
  cursor: default;
  text-decoration: none;
  color: var(--danger, #a33);
  font-weight: 500;
}

.map-landmark-bound-prose {
  max-height: 4.6em;
  overflow: hidden;
  font-size: 0.75rem;
  line-height: 1.4;
  color: var(--text-muted);
  font-family: var(--font-prose);
  -webkit-mask-image: linear-gradient(
    to bottom,
    #000 0%,
    #000 42%,
    transparent 100%
  );
  mask-image: linear-gradient(to bottom, #000 0%, #000 42%, transparent 100%);
}

.map-landmark-bound-prose-expanded {
  max-height: none;
  overflow: visible;
  -webkit-mask-image: none;
  mask-image: none;
}

.map-landmark-more {
  appearance: none;
  align-self: flex-start;
  border: 0;
  background: transparent;
  padding: 0;
  margin: 0;
  font: inherit;
  font-size: 0.75rem;
  color: var(--text-muted);
  text-decoration: underline;
  text-decoration-color: color-mix(
    in oklch,
    var(--text-muted) 45%,
    transparent
  );
  text-underline-offset: 0.12em;
  cursor: pointer;
}

.map-landmark-more:hover {
  color: var(--text);
  text-decoration-color: var(--text-muted);
}

.map-landmark-bound-prose :deep(> :first-child) {
  margin-top: 0;
}

.map-landmark-bound-prose :deep(> :last-child) {
  margin-bottom: 0;
}

.map-landmark-bound-prose :deep(h1),
.map-landmark-bound-prose :deep(h2),
.map-landmark-bound-prose :deep(h3) {
  font-size: 0.8125rem;
  font-weight: 600;
  margin: 0.4em 0 0.15em;
  color: var(--text);
}

.map-landmark-bound-prose :deep(p),
.map-landmark-bound-prose :deep(ul),
.map-landmark-bound-prose :deep(ol) {
  margin: 0.25em 0;
}

.map-landmark-bound-prose :deep(ul),
.map-landmark-bound-prose :deep(ol) {
  padding-left: 1.1em;
}

.map-landmark-list {
  list-style: none;
  margin: 0 0 0.4rem;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
}

.map-landmark-list-item {
  min-width: 0;
}

.map-landmark-broken-actions {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.3rem;
  padding: 0 0 0.15rem;
}

@media (max-width: 640px) {
  .map-landmark-panel {
    top: auto;
    left: 0;
    right: 0;
    width: 100%;
    max-height: 45%;
    border-left: 0;
    border-top: 1px solid var(--line);
    border-radius: 0.5rem 0.5rem 0 0;
  }
}
</style>
